import { useMutation, useQuery } from '@apollo/client'
import { useState } from 'react'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'
import type { z } from 'zod'

import { CREATE_USER, INVITE_USER_TO_WORKSPACE } from '../../../../../../data/mutations/user'
import { DOES_USER_EXIST } from '../../../../../../data/queries/members'
import { useAuth } from '../../../../../../hooks/use-auth'
import { useUserQuery } from '../../../../../../hooks/use-user-query'
import type { UserDetails } from '../../../../../../types/user'
import { CreatedVia, MembershipAccessType } from '../../../../../../types/user'
import { isEmail } from '../../../../../../utils/common'
import type { emailPhoneInputSchema } from '../../../shared/email-phone-form'
import type { AddUserProps } from '../../types'
import { useWorkspaceMembersAndInvitations } from './use-members-invitations'

/**
 * This custom hook is used to handle the selection of team members for a workspace.
 * It allows the user to search for and select team members from the workspace.
 * It also allows the user to invite new members to the workspace.
 */
export const useTeamMemberSelection = (
  workspaceID: string,
  selectedUsers: UserDetails[],
  setOpenInvitePopup: (value: boolean) => void,
  handleAddTeamMember: ({
    id,
    emailOrPhone,
    user,
    membership,
    userWorkspaceID,
    workspace
  }: AddUserProps) => Promise<void>,
  memoizedSuggestedUsers: { user: UserDetails }[],
  memoizedInvitedUsers: { user: UserDetails }[]
) => {
  const customHeaderContext = {
    headers: workspaceID ? { 'ws-id': workspaceID } : {}
  }
  const [userInput, setUserInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [createUser] = useMutation(CREATE_USER)
  const accessType = MembershipAccessType.MEMBER
  const { myDetails } = useUserQuery()
  const { selectedWorkspace } = useAuth()
  const { refetchWorkspaceInvitations } = useWorkspaceMembersAndInvitations(
    workspaceID,
    selectedWorkspace?.workspace.id || ''
  )

  const [inviteUserToWorkspace] = useMutation(INVITE_USER_TO_WORKSPACE, {
    context: {
      ...customHeaderContext
    },
    onCompleted() {
      refetchWorkspaceInvitations()
    }
  })

  const handlePopup = (message?: string, status = 'error') => {
    setLoading(false)
    setOpenInvitePopup(false)
    if (message) {
      status === 'error' ? toast.error(message) : toast.success(message)
    }
  }

  const handleInviteToWorkspace = ({
    id,
    emailOrPhone,
    accessType
  }: {
    accessType: MembershipAccessType
    emailOrPhone: string
    id: string
  }) => {
    const input = {
      accessType: accessType,
      countryCode: '+1',
      email: isEmail(emailOrPhone) ? emailOrPhone : undefined,
      invitedByID: myDetails?.id,
      phone: !isEmail(emailOrPhone) ? emailOrPhone : emailOrPhone.slice(2),
      userID: id,
      workspaceID: workspaceID
    }
    inviteUserToWorkspace({ variables: input })
  }

  const handleExistingUser = async (exists: UserDetails) => {
    const { id, name, photo, email, phone } = exists
    const user = { email, id, name, phone, photo }

    if (selectedUsers.some((member) => member.id === id)) {
      handlePopup()
      return
    }

    const suggestedUser =
      memoizedSuggestedUsers.find(
        (suggestedTeamMember: { user: { id: string } }) => suggestedTeamMember?.user?.id === id
      ) || memoizedInvitedUsers.find((invitedUser: { user: { id: string } }) => invitedUser?.user?.id === id)
    if (!suggestedUser) {
      handleInviteToWorkspace({ accessType, emailOrPhone: user.email || user.phone || '', id })
    }
    try {
      await handleAddTeamMember({
        emailOrPhone: user.email || user.phone || '',
        id,
        user,
        userWorkspaceID: workspaceID
      })
      handlePopup()
    } catch (error) {
      // Error is already handled by onError callback in useTeamMemberMutations
      // which will close the popup and show error toast
      handlePopup()
    }
  }

  const handleNewUser = (cleanedEmailPhone: string) => {
    const id = uuidv4()
    createUser({
      variables: {
        createdVia: CreatedVia.ChaineWebApp,
        emailOrPhone: cleanedEmailPhone,
        userID: id
      }
    })
    handleInviteToWorkspace({ accessType, emailOrPhone: cleanedEmailPhone, id })
    handleAddTeamMember({ emailOrPhone: cleanedEmailPhone, id })
    handlePopup()
  }

  //To-do: Replace this with useLazyQuery
  const { refetch: userExists } = useQuery(DOES_USER_EXIST, {
    fetchPolicy: 'network-only',
    skip: true
  })

  const handleSubmit = async (data: z.infer<typeof emailPhoneInputSchema>) => {
    const cleanedEmailPhone = data.cleanedEmailPhone?.trim()
    if (cleanedEmailPhone) {
      setUserInput(cleanedEmailPhone)
      setLoading(true)
      try {
        const data = await userExists({ emailOrPhone: cleanedEmailPhone })
        if (data) {
          if (data.data.userExists) {
            handleExistingUser(data.data.userExists)
          } else {
            handleNewUser(cleanedEmailPhone)
          }
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to get upload URL. Please try again.'
        setLoading(false)
        toast.error(errorMessage)
      }
    }
  }

  return {
    handleSubmit,
    loading,
    setUserInput,
    userInput
  }
}
